import { z } from 'zod';
export const financeImportCapability = 'finance.import-reconciliation.v1';
export const financeImportManifestVersion = 'finance-source-manifest.v1';
export const financeImportMappingVersion = 'finance-normalized-v1';
export const financeImportRunStatusSchema = z.enum([
    'prepared', 'applying', 'reconciled', 'failed', 'rolled-back',
]);
export const financeImportCountsByEntitySchema = z.strictObject({
    invoice: z.number().int().nonnegative().safe(),
    payment: z.number().int().nonnegative().safe(),
    expense: z.number().int().nonnegative().safe(),
    'overhead-cost-center': z.number().int().nonnegative().safe(),
    'overhead-account-category': z.number().int().nonnegative().safe(),
    'overhead-bill': z.number().int().nonnegative().safe(),
    'overhead-bill-occurrence': z.number().int().nonnegative().safe(),
    'overhead-allocation-policy': z.number().int().nonnegative().safe(),
    'overhead-allocation-run': z.number().int().nonnegative().safe(),
    'reconciliation-statement': z.number().int().nonnegative().safe(),
    'payment-processor-entry': z.number().int().nonnegative().safe(),
    'payment-payout': z.number().int().nonnegative().safe(),
    'payment-dispute': z.number().int().nonnegative().safe(),
    'commission-rule': z.number().int().nonnegative().safe(),
    'commission-payout': z.number().int().nonnegative().safe(),
});
const unsignedTotal = z.number().int().nonnegative().safe();
const signedTotal = z.number().int().safe();
export const financeImportDomainTotalsSchema = z.strictObject({
    invoiceVersionCount: unsignedTotal,
    invoiceLineCount: unsignedTotal,
    invoiceWriteOffCount: unsignedTotal,
    invoiceSubtotalCents: unsignedTotal,
    invoiceDiscountCents: unsignedTotal,
    invoiceTaxCents: unsignedTotal,
    invoiceTotalCents: unsignedTotal,
    invoicePaidCents: unsignedTotal,
    invoiceWrittenOffCents: unsignedTotal,
    paymentVersionCount: unsignedTotal,
    paymentAmountCents: unsignedTotal,
    expenseVersionCount: unsignedTotal,
    expenseLineCount: unsignedTotal,
    expenseSubtotalCents: unsignedTotal,
    expenseTaxCents: unsignedTotal,
    expenseTotalCents: unsignedTotal,
    overheadEntityVersionCount: unsignedTotal,
    overheadAllocationItemCount: unsignedTotal,
    overheadExpectedCents: unsignedTotal,
    overheadActualCents: unsignedTotal,
    overheadAnnualCents: unsignedTotal,
    overheadAnnualRevenueBasisCents: unsignedTotal,
    overheadAllocationBasisCents: unsignedTotal,
    overheadAllocatedCents: unsignedTotal,
    statementVersionCount: unsignedTotal,
    statementTransactionCount: unsignedTotal,
    statementDebitCents: unsignedTotal,
    statementCreditCents: unsignedTotal,
    statementMatchedCount: unsignedTotal,
    processorEntryCount: unsignedTotal,
    processorGrossCents: signedTotal,
    processorFeeCents: signedTotal,
    processorNetCents: signedTotal,
    paymentPayoutCount: unsignedTotal,
    paymentPayoutCents: unsignedTotal,
    paymentDisputeCount: unsignedTotal,
    paymentDisputeCents: unsignedTotal,
    commissionRuleVersionCount: unsignedTotal,
    commissionPayoutVersionCount: unsignedTotal,
    commissionBasisCents: unsignedTotal,
    commissionPayoutCents: unsignedTotal,
});
export const financeImportRunSummarySchema = z.strictObject({
    contractVersion: z.literal(financeImportCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    manifestVersion: z.literal(financeImportManifestVersion),
    mappingVersion: z.literal(financeImportMappingVersion),
    sourceSystem: z.literal('crest-crm'),
    sourceRepository: z.literal('jakeflora-cell/crest-crm'),
    sourceCommitSha: z.string().regex(/^[a-f0-9]{40}$/),
    sourceTreeSha: z.string().regex(/^[a-f0-9]{40}$/),
    sourceSchemaSha256: z.string().regex(/^[a-f0-9]{64}$/),
    sourceManifestSha256: z.string().regex(/^[a-f0-9]{64}$/),
    capturedAt: z.string().datetime({ offset: true }),
    tenantExternalKey: z.string().trim().min(1).max(240),
    runKind: z.enum(['base', 'final-delta']),
    sourceEpoch: z.string().trim().min(1).max(240),
    parentSourceSha256: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
    sourceSha256: z.string().regex(/^[a-f0-9]{64}$/),
    planSha256: z.string().regex(/^[a-f0-9]{64}$/),
    status: financeImportRunStatusSchema,
    sourceRows: z.number().int().nonnegative(),
    plannedRows: z.number().int().nonnegative(),
    rejectedRows: z.number().int().nonnegative(),
    appliedRows: z.number().int().nonnegative().nullable(),
    skippedRows: z.number().int().nonnegative().nullable(),
    tombstonedRows: z.number().int().nonnegative().nullable(),
    targetSha256: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
    countsByEntity: financeImportCountsByEntitySchema.nullable(),
    domainTotals: financeImportDomainTotalsSchema.nullable(),
    failureReasonCode: z.string().trim().min(3).max(160).nullable(),
    rollbackReason: z.string().trim().min(10).max(1_000).nullable(),
    startedByMembershipId: z.string().uuid(),
    startedAt: z.string().datetime({ offset: true }),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    rolledBackAt: z.string().datetime({ offset: true }).nullable(),
});
//# sourceMappingURL=finance-import.contract.js.map